import type { Market, NutrientId, Profile } from "./types";

/**
 * 2016 Daily Value tables — 21 CFR 101.9(c)(8)(iv) RDIs + (c)(9) DRVs.
 *
 * Transcribed verbatim from notes/COMPLIANCE-ENGINE-SPEC.md §1.3 and the §6 data model.
 * These are the legally load-bearing numbers — change only against the eCFR.
 *
 * CRITICAL (verified live eCFR 2026-06-01): US potassium DV is 4,700 mg and has NOT
 * changed. Only Canada moves to 3,400 mg in 2026 — do NOT apply the Canadian value to
 * US labels.
 *
 * `null` (or "id absent from the column") means no Daily Value is established for that
 * nutrient (e.g. energy/calories, trans fat, total sugars).
 */

// DRVs (food components) + RDIs (vitamins/minerals), adults & children ≥4, 2,000 kcal basis.
const US_ADULT: Partial<Record<NutrientId, number>> = {
  fat_total: 78,
  fat_sat: 20,
  cholesterol: 300,
  sodium: 2300,
  carb_total: 275,
  fiber: 28,
  protein: 50,
  sugars_added: 50,
  vitamin_d: 20,
  calcium: 1300,
  iron: 18,
  potassium: 4700, // US value — NOT the Canadian 3400.
};

// Children 1–3 yr DRVs (1,000 kcal basis) + RDIs (spec §1.3 + §6).
// Cholesterol DRV is 300 mg for ≥1 yr (eCFR 101.9(c)(9)); not separately reduced for 1–3.
const US_CHILD_1_3: Partial<Record<NutrientId, number>> = {
  fat_total: 39,
  fat_sat: 10,
  cholesterol: 300,
  sodium: 1500,
  carb_total: 150,
  fiber: 14,
  protein: 13,
  sugars_added: 25,
  vitamin_d: 15,
  calcium: 700,
  iron: 7,
  potassium: 3000,
};

// Infants 0–12 mo — mandatory-micro RDIs (spec §6). The macro DRVs for infants are not
// used by the US Nutrition Facts mandatory set in M1; only the four micros are encoded.
const US_INFANT_0_12MO: Partial<Record<NutrientId, number>> = {
  vitamin_d: 10,
  calcium: 260,
  iron: 11,
  potassium: 700,
};

// Pregnant/lactating column (spec §1.3 + §6). Distinct values: iron 27, potassium 5100,
// vitamin D 15, protein 71. Other macros inherit the adult DRVs.
const US_PREGNANT_LACTATING: Partial<Record<NutrientId, number>> = {
  ...US_ADULT,
  protein: 71,
  vitamin_d: 15,
  calcium: 1300,
  iron: 27,
  potassium: 5100,
};

const US: Record<Profile, Partial<Record<NutrientId, number>>> = {
  adult: US_ADULT,
  infant_0_12mo: US_INFANT_0_12MO,
  child_1_3: US_CHILD_1_3,
  pregnant_lactating: US_PREGNANT_LACTATING,
};

// EU/CA/UK profiles slot in here as those markets ship (M-Launch / M-Growth).
const TABLE: Partial<Record<Market, Record<Profile, Partial<Record<NutrientId, number>>>>> = {
  US,
};

/**
 * Returns the Daily Value (in the nutrient's canonical unit) for the given
 * nutrient/market/profile, or `null` if no DV is established.
 */
export function dailyValue(
  id: NutrientId,
  market: Market,
  profile: Profile,
): number | null {
  const column = TABLE[market]?.[profile];
  if (!column) return null;
  const dv = column[id];
  return dv ?? null;
}
